import { useState } from "react";
import { useTranslation } from "react-i18next";
import { extractErrorMessage } from "@/shared/utils";
import { useHiringDownloadApplicationProfilePreviewMutation } from "../api";
import type { PromptState } from "./useHiringModals";

export interface UseHiringDownloadsReturn {
  downloadingId: number | null;
  isDownloading: boolean;
  handleDownloadProfile: (id: number) => Promise<void>;
  setPrompt: (state: PromptState | null) => void;
  previewUrl: string | null;
  previewFileName: string | null;
  closePreview: () => void;
}

/** @deprecated Candidate-stage "Открыть личный листок" download. The личный листок is produced only after the worker completes and signs step 2; this wraps the deprecated /api/hr/applications/{id}/preview-profile/ endpoint. Do not surface in new UI. */
export function useHiringDownloads(setPrompt: (state: PromptState | null) => void): UseHiringDownloadsReturn {
  const { t } = useTranslation("HiringPage");

  const [downloadProfile] = useHiringDownloadApplicationProfilePreviewMutation();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string | null>(null);

  const isDownloading = downloadingId !== null;

  function closePreview() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewFileName(null);
  }

  async function handleDownloadProfile(id: number) {
    if (isDownloading) return;

    setDownloadingId(id);
    try {
      const { blob, filename } = await downloadProfile(id).unwrap();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewFileName(filename || t("downloads.applicationProfileFileName", { id }));
      setDownloadingId(null);
    } catch (err: unknown) {
      setDownloadingId(null);
      const errorMessage = extractErrorMessage(err, "messages.downloadFailed", t);
      setPrompt({
        title: t("messages.errorTitle"),
        text: errorMessage,
        variant: "error",
      });
    }
  }

  return {
    downloadingId,
    isDownloading,
    handleDownloadProfile,
    setPrompt,
    previewUrl,
    previewFileName,
    closePreview,
  };
}
